import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate, matchPath } from "react-router-dom";
import {
  Bell,
  Wallet,
  PanelLeftOpen,
  UserCircle2,
  Settings,
  LifeBuoy,
  Bug,
  LogOut,
  Send,
  Sun,
  Moon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { UserAvatar, resolveAvatarUrl } from "@/components/UserAvatar";
import { api, apiErrorMessage, uploadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";

// Route → Page title mapping (auto-derived)
const TITLE_PATTERNS = [
  { path: "/home", title: "Home" },
  { path: "/dashboard", title: "Dashboard" },
  { path: "/campaigns/new", title: "New Campaign" },
  { path: "/campaigns/:campaignId/edit", title: "Edit Campaign" },
  { path: "/campaigns/:campaignId", title: "Campaign" },
  { path: "/campaigns", title: "Campaigns" },
  { path: "/my-campaigns", title: "My Campaigns" },
  { path: "/earnings", title: "Earnings" },
  { path: "/wallet", title: "Earnings" },
  { path: "/payouts", title: "Payouts" },
  { path: "/socials", title: "Accounts" },
  { path: "/leaderboard", title: "Leaderboard" },
  { path: "/insights", title: "Insights" },
  { path: "/settings", title: "Settings" },
  { path: "/profile", title: "Profile" },
  { path: "/help", title: "Help" },
  { path: "/admin/:adminTab", title: "Admin" },
  { path: "/admin", title: "Admin" },
  { path: "/mod/:modTab", title: "Moderator" },
  { path: "/mod", title: "Moderator" },
];

function derivePageTitle(pathname) {
  for (const p of TITLE_PATTERNS) {
    if (matchPath({ path: p.path, end: true }, pathname)) return p.title;
  }
  return "";
}

export function DesktopTopBar({ collapsed, onExpand, onOpenNotifications, unreadCount = 0 }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { me, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [profileOpen, setProfileOpen] = useState(false);
  const [bugOpen, setBugOpen] = useState(false);
  const [bugMessage, setBugMessage] = useState("");
  const [bugFile, setBugFile] = useState(null);
  const [bugSending, setBugSending] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const profileRef = useRef(null);

  const title = derivePageTitle(location.pathname);

  // Fetch wallet balance
  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const res = await api.get("/wallet");
        if (!alive) return;
        setWalletBalance(Number(res.data?.available_balance || 0));
      } catch {
        if (alive) setWalletBalance(0);
      }
    }
    load();
    const t = window.setInterval(load, 60000);
    return () => { alive = false; window.clearInterval(t); };
  }, [me?.id]);

  // Close profile dropdown on outside click
  useEffect(() => {
    if (!profileOpen) return;
    function onClick(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [profileOpen]);

  async function submitBug() {
    if (!bugMessage.trim()) return;
    setBugSending(true);
    try {
      let attachment = null;
      if (bugFile) {
        const uploaded = await uploadFile(bugFile, "bug-report");
        attachment = uploaded.id;
      }
      await api.post("/contact", {
        name: "FeedBacks",
        email: me?.email || "hello@clippingbase.com",
        subject: "Report Bug",
        message: `User: ${me?.discord_username || me?.username || "Unknown"}\n\n${bugMessage.trim()}`,
        attachment_file_id: attachment,
      });
      toast({ title: "Bug report sent", description: "Thanks for reporting this issue." });
      setBugMessage(""); setBugFile(null); setBugOpen(false); setProfileOpen(false);
    } catch (e) {
      toast({ title: "Error", description: apiErrorMessage(e) });
    } finally {
      setBugSending(false);
    }
  }

  return (
    <header
      data-testid="desktop-topbar"
      className="sticky top-0 z-30 flex h-[60px] items-center justify-between gap-4 border-b border-white/[0.06] bg-[#141414]/95 px-6 backdrop-blur-xl"
    >
      {/* Left: expand toggle (only when collapsed) + page title */}
      <div className="flex min-w-0 items-center gap-3">
        {collapsed && (
          <button
            type="button"
            data-testid="desktop-topbar-expand"
            onClick={onExpand}
            title="Expand sidebar"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-zinc-400 hover:bg-white/[0.06] hover:text-white"
          >
            <PanelLeftOpen size={17} />
          </button>
        )}
        <h1
          data-testid="desktop-topbar-title"
          className="truncate text-[17px] font-semibold tracking-tight text-white"
        >
          {title}
        </h1>
      </div>

      {/* Right: notifications, wallet pill, avatar */}
      <div className="flex shrink-0 items-center gap-3">
        {/* Theme toggle */}
        <Button
          data-testid="desktop-topbar-theme-toggle"
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-full text-zinc-400 transition-all hover:bg-white/[0.06] hover:text-zinc-100"
          onClick={toggleTheme}
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        >
          {theme === "dark" ? <Moon size={16} /> : <Sun size={16} />}
        </Button>

        {/* Notifications */}
        <div className="relative">
          <button
            data-testid="desktop-topbar-notifications"
            type="button"
            onClick={() => onOpenNotifications?.()}
            title="Notifications"
            className="grid h-9 w-9 place-items-center rounded-full border border-white/[0.06] bg-[#1a1a1a] text-zinc-300 transition-colors hover:bg-white/[0.06] hover:text-white"
          >
            <Bell size={16} />
          </button>
          {unreadCount > 0 && (
            <div
              data-testid="desktop-topbar-notifications-badge"
              className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-[#c8ff00] px-1 text-[10px] font-bold text-black shadow-[0_0_10px_rgba(200,255,0,0.55)]"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </div>
          )}
        </div>

        {/* Wallet pill → /payouts */}
        <NavLink
          to="/payouts"
          data-testid="desktop-topbar-wallet"
          title="Payouts"
          className="flex h-9 items-center gap-2 rounded-full border border-white/[0.06] bg-[#1a1a1a] pl-2.5 pr-3 text-white transition-colors hover:border-[#c8ff00]/30 hover:bg-[#c8ff00]/[0.06]"
        >
          <span className="grid h-6 w-6 place-items-center rounded-full bg-[#c8ff00]/12">
            <Wallet size={13} className="text-[#c8ff00]" />
          </span>
          <span
            data-testid="desktop-topbar-wallet-balance"
            className="text-[13.5px] font-semibold text-[#c8ff00]"
          >
            ${walletBalance == null ? "0.00" : walletBalance.toFixed(2)}
          </span>
        </NavLink>

        {/* Avatar dropdown */}
        {me && (
          <div ref={profileRef} className="relative">
            <button
              type="button"
              data-testid="desktop-topbar-avatar-button"
              onClick={() => setProfileOpen((v) => !v)}
              className="flex items-center rounded-full transition-transform hover:scale-[1.04]"
              title={me?.discord_username || me?.username || "Profile"}
            >
              <UserAvatar
                testId="desktop-topbar-avatar-image"
                user={me}
                src={resolveAvatarUrl(me)}
                size={36}
              />
            </button>

            {profileOpen && (
              <div
                data-testid="desktop-topbar-profile-popup"
                className="absolute right-0 top-[calc(100%+12px)] z-50 w-[280px] origin-top-right overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0c0c0c]/98 p-2 shadow-[0_20px_60px_rgba(0,0,0,0.6)] backdrop-blur-xl"
              >
                <div className="flex items-center gap-3 rounded-xl px-3 py-3">
                  <UserAvatar
                    user={me}
                    src={resolveAvatarUrl(me)}
                    size={44}
                    testId="desktop-topbar-popup-avatar"
                    className="!border-white/10"
                  />
                  <div className="min-w-0 flex-1">
                    <div data-testid="desktop-topbar-popup-username" className="truncate text-[14px] font-semibold text-white">
                      {me?.discord_username || me?.username || "—"}
                    </div>
                    <div data-testid="desktop-topbar-popup-email" className="truncate text-[12px] text-zinc-500">
                      {me?.email || "—"}
                    </div>
                  </div>
                </div>

                <div className="my-1.5 h-px bg-white/[0.06]" />

                <div className="space-y-0.5">
                  <button
                    data-testid="desktop-topbar-popup-profile"
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-[13.5px] font-medium text-zinc-400 transition-colors hover:bg-white/[0.05] hover:text-white"
                    onClick={() => { navigate("/profile"); setProfileOpen(false); }}
                  >
                    <UserCircle2 size={16} className="shrink-0" /> Profile
                  </button>
                  <button
                    data-testid="desktop-topbar-popup-settings"
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-[13.5px] font-medium text-zinc-400 transition-colors hover:bg-white/[0.05] hover:text-white"
                    onClick={() => { navigate("/settings"); setProfileOpen(false); }}
                  >
                    <Settings size={16} className="shrink-0" /> Settings
                  </button>
                  <button
                    data-testid="desktop-topbar-popup-help"
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-[13.5px] font-medium text-zinc-400 transition-colors hover:bg-white/[0.05] hover:text-white"
                    onClick={() => { navigate("/help"); setProfileOpen(false); }}
                  >
                    <LifeBuoy size={16} className="shrink-0" /> Help
                  </button>
                  <button
                    data-testid="desktop-topbar-popup-report-bug"
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-[13.5px] font-medium text-zinc-400 transition-colors hover:bg-white/[0.05] hover:text-white"
                    onClick={() => setBugOpen(true)}
                  >
                    <Bug size={16} className="shrink-0" /> Report Bug
                  </button>
                </div>

                <div className="my-1.5 h-px bg-white/[0.06]" />

                <button
                  data-testid="desktop-topbar-popup-logout"
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-[13.5px] font-medium text-rose-400 transition-colors hover:bg-rose-500/10"
                  onClick={() => { logout(); }}
                >
                  <LogOut size={16} className="shrink-0" /> Log out
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Report Bug dialog */}
      <Dialog open={bugOpen} onOpenChange={setBugOpen}>
        <DialogContent data-testid="desktop-topbar-report-bug-dialog" className="max-w-md border-[#1a1a1a] bg-[#0a0a0a] text-white">
          <DialogHeader><DialogTitle>Report Bug</DialogTitle><DialogDescription>Tell us what happened. A screenshot is optional.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Textarea data-testid="desktop-topbar-report-bug-message" value={bugMessage} onChange={(e) => setBugMessage(e.target.value)} placeholder="What issue did you face?" className="min-h-[120px] border-[#1a1a1a] bg-black text-white" />
            <label data-testid="desktop-topbar-report-bug-picture" className="block cursor-pointer rounded-lg border border-dashed border-[#222] bg-black px-3 py-3 text-sm text-zinc-500 hover:border-[#c8ff00]/40">
              {bugFile ? bugFile.name : "Providing a picture would help us best"}
              <Input type="file" accept="image/*" className="hidden" onChange={(e) => setBugFile(e.target.files?.[0] || null)} />
            </label>
            <Button data-testid="desktop-topbar-report-bug-submit" className="w-full rounded-xl bg-[#c8ff00] font-bold text-black hover:bg-[#b8ef00] active:scale-[0.97]" disabled={!bugMessage.trim() || bugSending} onClick={submitBug}>
              <Send size={14} /> {bugSending ? "Sending..." : "Send Report"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
}
